import React from "react";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CustomLabel from "./CustomLabel";
import CustomTextField from "./CustomTextField";
import { COLORS } from "../../assets/theme";

interface CustomDateTimePickerProps {
  label?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  error?: boolean;
  helperText?: string;
  highlightLabelStar?: boolean;
  placeholder?: string;
  WitOutTime?: boolean; 
  goldTheme?: boolean;
}

// Applied to the picker layout, which wraps the calendar in both the desktop popper and mobile dialog.
const goldLayoutSx = {
  "& .MuiPickersDay-root.Mui-selected, & .MuiPickersYear-yearButton.Mui-selected, & .MuiPickersMonth-monthButton.Mui-selected":
    {
      color: COLORS.iconColor,
      backgroundColor: COLORS.GOLD,
      "&:hover, &:focus": {
        backgroundColor: COLORS.GOLD,
      },
    },
  "& .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected)": {
    borderColor: COLORS.GOLD,
  },
  "& .MuiPickersLayout-actionBar .MuiButton-root": {
    color: COLORS.GOLD,
  },
};

const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({
  label,
  value,
  onChange,
  error = false,
  helperText,
  highlightLabelStar = false,
  placeholder = "Enter Date and Time",
  WitOutTime = false,
  goldTheme = false,
}) => {
  const dateTimeValue = value
    ? DateTime.fromJSDate(value, { zone: "UTC" })
    : null;

  const PickerComponent = WitOutTime ? DatePicker : DateTimePicker;

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {label && (
        <CustomLabel
          text={label}
          highlightLabelStar={highlightLabelStar}
          sx={{ marginBottom: "5px" }}
        />
      )}
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <PickerComponent
          value={dateTimeValue}
          onChange={(newValue) => onChange(newValue ? newValue.toJSDate() : null)}
          slots={{ textField: CustomTextField }}
          timezone="UTC"
          slotProps={{
            textField: {
              ...({
                bgColor: COLORS.WhiteBackground,
                error,
                helperText,
                fullWidth: true,
                placeholder,
              } as any),
            },
            ...(goldTheme && { layout: { sx: goldLayoutSx } }),
          }}
        />
      </LocalizationProvider>
    </div>
  );
};

export default CustomDateTimePicker;